export const MEETING_TYPE_OPTIONS = [
    { value: 'online', label: 'Nova Meeting' },
    { value: 'hybrid_zoom', label: 'Zoom Meeting' },
    { value: 'hybrid_google_meet', label: 'Google Meeting' },
];

export const formatMeetingDate = (dateStr) => {
    if (!dateStr) return '';
    try {
        const d = new Date(dateStr.replace(/-/g, "/"));
        if (isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
        return dateStr;
    }
};

export const formatStartTimeOnly = (startStr) => {
    if (!startStr) return '';
    try {
        const s = new Date(startStr.replace(/-/g, "/"));
        if (isNaN(s.getTime())) return startStr;
        return s.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
        return startStr;
    }
};

export const getMeetingPlatformLabel = (meeting) => {
    const type = meeting?.meeting_type;
    if (type === 'hybrid_zoom') return 'Zoom';
    if (type === 'hybrid_google_meet') return 'Google Meet';
    if (type === 'physical') return meeting?.room_name || 'Physical Room';
    return 'eNova';
};

export const toDateKey = (dateStrOrDate) => {
    if (!dateStrOrDate) return '';
    const d = dateStrOrDate instanceof Date
        ? dateStrOrDate
        : new Date(String(dateStrOrDate).replace(/-/g, '/'));
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const buildMonthGrid = (cursorDate) => {
    const year = cursorDate.getFullYear();
    const month = cursorDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startOffset = firstDay.getDay(); // Sunday = 0
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    const cells = [];

    for (let i = 0; i < startOffset; i++) {
        const day = daysInPrevMonth - startOffset + 1 + i;
        cells.push({ date: new Date(year, month - 1, day), inCurrentMonth: false });
    }
    for (let day = 1; day <= daysInMonth; day++) {
        cells.push({ date: new Date(year, month, day), inCurrentMonth: true });
    }
    let nextMonthDay = 1;
    while (cells.length % 7 !== 0) {
        cells.push({ date: new Date(year, month + 1, nextMonthDay++), inCurrentMonth: false });
    }
    return cells;
};

export const calculateDuration = (meetingOrStart, endStr, durationMins) => {
    let startStr = meetingOrStart;
    let status = null;
    let updatedAt = null;

    if (typeof meetingOrStart === 'object' && meetingOrStart !== null) {
        startStr = meetingOrStart.start_time;
        endStr = meetingOrStart.end_time;
        durationMins = meetingOrStart.duration;
        status = meetingOrStart.status;
        updatedAt = meetingOrStart.updated_at;
    }

    if ((status === 'completed' || endStr) && startStr) {
        try {
            const s = new Date(startStr.replace(/-/g, "/"));
            if (!isNaN(s.getTime())) {
                let endTargetStr = endStr;
                if (updatedAt) {
                    const uDate = new Date(updatedAt.replace(/-/g, "/"));
                    const eDate = endStr ? new Date(endStr.replace(/-/g, "/")) : null;
                    if (eDate && (eDate.getTime() - s.getTime() === 3600000) && uDate > s && uDate < eDate) {
                        endTargetStr = updatedAt;
                    }
                }
                const e = endTargetStr ? new Date(endTargetStr.replace(/-/g, "/")) : null;
                if (e && !isNaN(e.getTime())) {
                    const diffMs = e.getTime() - s.getTime();
                    if (diffMs > 0) {
                        const diffMins = Math.round(diffMs / (1000 * 60));
                        if (diffMins < 1) return `1 min`;
                        if (diffMins < 60) return `${diffMins} mins`;
                        const hrs = Math.floor(diffMins / 60);
                        const mins = diffMins % 60;
                        return `${hrs} ${hrs === 1 ? 'hour' : 'hours'}${mins > 0 ? ` ${mins} mins` : ''}`;
                    }
                }
            }
        } catch (err) {
            // fallback
        }
    }

    if (durationMins && Number(durationMins) > 0) {
        const m = Number(durationMins);
        if (m < 60) return `${m} mins`;
        const hrs = Math.floor(m / 60);
        const mins = m % 60;
        return `${hrs} ${hrs === 1 ? 'hour' : 'hours'}${mins > 0 ? ` ${mins} mins` : ''}`;
    }
    return null;
};

export const parseGuestEmails = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
        return value
            .flatMap((item) => parseGuestEmails(item))
            .filter(Boolean);
    }
    return String(value)
        .split(/[,;\n\r]+/)
        .map((e) => e.trim())
        .filter((e) => e && e.includes('@') && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
};

export const buildLinkModeDateTimes = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    const timePart = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:00`;
    const start_time = `${y}-${m}-${d} ${timePart}`;
    const later = new Date(now.getTime() + 60 * 60 * 1000);
    const end_time = `${later.getFullYear()}-${String(later.getMonth() + 1).padStart(2, '0')}-${String(later.getDate()).padStart(2, '0')} ${String(later.getHours()).padStart(2, '0')}:${String(later.getMinutes()).padStart(2, '0')}:00`;
    return { start_time, end_time };
};

export const getEnovaMeetingLink = (meetingId) => `${window.location.origin}/meetings?room=${meetingId}`;

export const getMeetingShareLink = (meeting) => {
    if (!meeting) return '';
    const isHybrid = meeting.meeting_type === 'hybrid_zoom' || meeting.meeting_type === 'hybrid_google_meet' || meeting.meeting_type === 'hybrid';
    if (isHybrid) {
        return meeting.external_meeting_url || '';
    }
    return getEnovaMeetingLink(meeting.id);
};
